// raw-fetch-ok-file: v0y4ge/voyage-fleet-management#676 -- pre-existing raw-fetch shadow API layer (studio-shell/coord-shell internal *-api.ts or identity/integrations-worker call), predates @v0y4ge/api-client convergence; tracked backlog, not a new violation.
// Studio frontend entry wrapper. The ENTIRE app UI is Claude Design's, mounted
// verbatim from ./cd-app (imported via tools/cd-import). This file is ONLY the
// tenant-native auth gate: it resolves the session (useWhoami against the
// identity worker), redirects unauthenticated users to the tenant login, and —
// once authenticated — renders CD's <App/> unchanged. No StudioShell wiring
// lives here anymore; CD owns the shell + every screen.

import { useEffect, useRef } from "react";
import { SignInScreen, type TenantBranding } from "@v0y4ge/studio-shell";
import { IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, tenantLoginUrl } from "./lib/signin-url";
import { useWhoami } from "./lib/useWhoami";
import { useDemoMode } from "./lib/useDemoMode";
import { useTenantTimeZone } from "./lib/tenantDateTime";
import { setAuthenticated, useAuth, ROLE_CATALOG, getCustomRoles, type CurrentUser } from "./cd-app/shell/access";
import { projectStudioUiCapabilities } from "@v0y4ge/api-contracts";
import CDApp from "./cd-app/App";
import { shouldMountStudioTour, isAttendHost } from "./tour-routes";

const WORKSPACE_DISPLAY_NAME = ((import.meta.env.VITE_WORKSPACE_DISPLAY_NAME as string | undefined) ?? "").trim();
const WORKSPACE_PRODUCT_NAME = ((import.meta.env.VITE_WORKSPACE_PRODUCT_NAME as string | undefined) ?? "").trim();
// Compose-emitted per-tenant demo_mode HINT (#655, demoted by #686) — baked at
// build time by skills/compose-tenant-deployment from tenant-manifest
// `tenant.demoMode`. This is NO LONGER the gate itself: it is only a
// first-paint seed for `useDemoMode`'s initial state (before its runtime
// probe resolves). The actual gate is `demo.enabled` below, which reads live
// D1 `tenants.demo_mode` at runtime via the SAME `/demo/personas` op CD's own
// persona chooser calls — see `./lib/useDemoMode.ts`. This makes the fleet
// admin toggle instantly authoritative with NO recompose/redeploy: a stale
// baked `true` can no longer keep the chooser showing after an operator
// flips demo_mode off (or vice versa), because the render/redirect decisions
// below always wait for the probe to settle rather than trusting this hint.
const DEMO_MODE_HINT = ((import.meta.env.VITE_DEMO_MODE as string | undefined) ?? "").trim().toLowerCase() === "true";

function buildLocalSignInBranding(): TenantBranding {
  return {
    displayName: WORKSPACE_DISPLAY_NAME || "Your Workspace",
    productName: WORKSPACE_PRODUCT_NAME || "Studio",
    heroTitle: "Plan, run, and learn from every event you put on.",
    heroBody: "One workspace for the calendar, guest list, communications, and numbers that prove it worked.",
    footerText: "Powered by JRNI",
  };
}

// Best-effort bridge from the identity worker membership-role taxonomy to
// cd-app's Studio permission-role taxonomy. Product/CD should reconcile these
// role models properly later.
//
// cd-drop3-ingest (studio-master@4fdb673, packet-3): this drop renamed CD's
// entire process-scoped role catalog (shell/access.ts ROLE_CATALOG) from the
// old "Admin"/"Editor"/"Approver"/"Viewer" vocabulary to five named Events
// roles: "Event Creator", "Event Coordinator", "Event Manager",
// "Event Approver", "System Administrator". Unmapped role strings resolve to
// zero effective capabilities (effectivePerms only awards perms for
// assignments.events values matching a real ROLE_CATALOG id) — every mapping
// below MUST target one of those five exact strings or the mapped user gets
// silently locked out of every capability.
//   owner / admin / platform_admin -> "System Administrator" (full access,
//     was "Admin").
//   member -> "Event Creator" (authors/edits content, was "Editor").
//   finance / support / reporting -> "Event Approver" (reviews/signs off,
//     was "Approver" — same semantic target, renamed).
// KNOWN GAP (pushback tracked alongside packet-3's tsc discrepancy): the new
// catalog has NO read-only-viewer-equivalent role — the closest non-admin
// role (Event Approver) still carries write-ish capabilities
// (events.unpublish, campaigns.approve), so granting it to a "viewer"
// membership would OVER-privilege them. Safer to fail closed: "viewer" (and
// any unrecognized role) maps to a label with NO ROLE_CATALOG entry, which
// effectivePerms resolves to zero capabilities rather than guessing.
// #702: demo-persona sessions (fresh sign-in AND reload alike) carry an
// already-canonical ROLE_CATALOG label in `session.role` (useWhoami's
// sessionFromWhoami prefers whoami's `user.role` -- the persona's real CD
// label, e.g. "Event Coordinator" -- over the collapsed MembershipRole slug).
// Passing one of those five exact labels through the slug switch below would
// mismatch every case (none of them lowercase to a known slug) and silently
// fall to "Viewer", re-collapsing the persona on every reload. So a role that
// is ALREADY one of the five canonical labels wins outright, unchanged, and
// only a non-canonical value (a real MembershipRole slug, for every
// non-demo/legacy session) goes through the slug mapping below.
// Session-refresh role-collapse fix (2026-07-28): this used to be a
// hand-listed set of ONLY the five Events labels, so a reload for any
// persona in another process (Queue Manager, Operations Admin, Personal
// Banker, ...) fell through to the slug switch below, matched nothing, and
// silently collapsed to "Viewer" -- zero capabilities, NoAccess screen.
// Derived from the live `ROLE_CATALOG` (every process, not just events) so a
// role that already IS a real catalog label always survives verbatim on
// reload, and a newly added catalog role never needs a matching edit here.
const STUDIO_ROLE_LABELS: ReadonlySet<string> = new Set(
  Object.values(ROLE_CATALOG).flatMap((defs) => defs.map((def) => def.id)),
);

export function mapMembershipRoleToStudioRole(role: string | null | undefined): CurrentUser["role"] {
  const trimmed = (role ?? "").trim();
  if (STUDIO_ROLE_LABELS.has(trimmed)) return trimmed;
  switch (trimmed.toLowerCase()) {
    case "owner":
    case "admin":
    case "platform_admin":
      return "System Administrator";
    case "member":
      return "Event Creator";
    case "finance":
    case "support":
    case "reporting":
      return "Event Approver";
    case "viewer":
    default:
      return "Viewer";
  }
}

const SESSIONLESS_STUDIO_ROUTES: ReadonlySet<string> = new Set([
  "/queue/display",
  "/queue/join",
]);

// A hit is a public attendee route (no tenant SSO redirect; falls straight
// through to <CDApp/>) when the pathname is an explicitly session-less Studio
// route, a `/public/*` Studio route, OR the request is on the
// `attend.<tenant>` host — where CD's anonymous attendee SPA is served at the
// bare `/<slug>` path (Option 2). The host arm is guarded to the literal
// `attend` first DNS label, so this stays a NO-OP on `studio.<tenant>` and
// every non-attend host.
export function isPublicAttendeeRoute(pathname: string, hostname: string = window.location.hostname): boolean {
  return SESSIONLESS_STUDIO_ROUTES.has(pathname)
    || isAttendHost(hostname)
    || !shouldMountStudioTour(pathname);
}

function displayNameFromSession(displayName: string | undefined, email: string): string {
  const trimmed = (displayName ?? "").trim();
  return trimmed || email.split("@")[0] || email;
}

function initialsFromName(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const parts = trimmed.includes("@") ? [trimmed.split("@")[0] ?? ""] : trimmed.split(/\s+/);
  return parts.map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

export function App() {
  const renderPublicAttendeeRoute = isPublicAttendeeRoute(window.location.pathname);
  // Public attend pages are anonymous by contract. Do not probe the identity
  // worker on them: a deliberate 401 is still a failed XHR to browsers and
  // rehearsal tooling, and the result cannot affect this render path.
  const whoami = useWhoami(IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, !renderPublicAttendeeRoute);
  // Runtime probe (#686): resolves the SAME live D1 `tenants.demo_mode` the
  // `/demo/personas` and `/demo/sign-in` backend routes already gate on, by
  // actually calling `getDemoPersonas()` — see `./lib/useDemoMode.ts` for the
  // full rationale (an inconclusive answer is retried, then shown as unreachable).
  const demo = useDemoMode(renderPublicAttendeeRoute ? "" : IDENTITY_BASE_URL, IDENTITY_TENANT_SLUG, DEMO_MODE_HINT);
  const loginUrl = tenantLoginUrl();
  const renderLocalSignIn = !IDENTITY_BASE_URL && window.location.pathname === "/sign-in";
  const session = whoami.session;

  // Demo persona-chooser gate (#655, made runtime-authoritative by #686): a
  // demo_mode=on tenant with a non-empty catalog mounts CDApp (below)
  // instead of redirecting to real SSO, once BOTH whoami and the demo probe have
  // resolved and whoami says "not authenticated". CDApp's own AuthGate
  // (shell/access.ts's `useAuth().signedIn`) then renders CD's OWN
  // <Signin/> persona chooser — it fetches personas and signs in via the
  // wired `getDemoPersonas`/`demoSignIn` ops on `@v0y4ge/api-client`
  // (see shell/access.ts's `fetchPersonas`/`signIn`), no host-side
  // duplication needed. This gate controls WHETHER an unauthenticated hit is allowed to reach
  // CDApp instead of being redirected — never WHICH sign-in component
  // renders once there.
  const demoSignInAvailable = demo.enabled && !demo.empty;
  const renderDemoSignIn =
    demoSignInAvailable &&
    !renderLocalSignIn &&
    !renderPublicAttendeeRoute &&
    !whoami.loading &&
    !demo.loading &&
    !whoami.authenticated;

  // The demo check failed every attempt (timeout, network error, 5xx), so this
  // signed-out visitor can be sent neither to the persona chooser nor to the
  // real login. CDApp is mounted with this state: its AuthGate hands it to CD's
  // sign-in screen, which shows "couldn't reach sign-in" with Try again (calling
  // the hook's retry(), and busy until that promise settles) and makes no demo
  // reads meanwhile. A successful retry clears it and the same mounted screen
  // shows the chooser. Only a signed-out visitor gets it: CD's sign-in screen
  // also covers a signed-in session while its landing route resolves, which a
  // failed demo check says nothing about.
  const renderDemoUnreachable =
    demo.unreachable &&
    !renderLocalSignIn &&
    !renderPublicAttendeeRoute &&
    !whoami.loading &&
    !whoami.authenticated;

  // Anchor the datetime runtime to the TENANT's configured zone before any
  // screen paints a timestamp. The public-config read is a tenant-scoped,
  // signed-in API call; it is idempotent and self-guards against duplicate fetches.
  //
  // Gated on the HOST's resolved session, the one signal for "signed in" in this
  // file. CD's in-page persona sign-in reaches it too: the effect below re-resolves
  // whoami as soon as `useAuth()` reports a person the host does not hold yet, so
  // a demo session and a reload carry the same session (and the same capability
  // list, which `setAuthenticated` hands cd-app). The key re-runs the lookup when
  // the signed-in tenant or person changes.
  const cdAuth = useAuth();
  const cdSignedIn = cdAuth.signedIn;
  const cdUserId = cdAuth.user?.id ?? "";
  useTenantTimeZone(
    !renderPublicAttendeeRoute && !whoami.loading && whoami.authenticated,
    `${session.tenant.id}:${session.user.id}`,
  );

  useEffect(() => {
    if (
      whoami.loading ||
      demo.loading ||
      whoami.authenticated ||
      renderLocalSignIn ||
      renderPublicAttendeeRoute ||
      demoSignInAvailable ||
      // The demo check could not get an answer (timeout, network error, 5xx).
      // Whether this tenant is a demo tenant is unknown, so do not send the
      // visitor to the real login; the render below shows CD's sign-in screen
      // in its unreachable state, with Try again, instead.
      demo.unreachable
    ) {
      return;
    }
    window.location.assign(loginUrl);
  }, [
    loginUrl,
    renderLocalSignIn,
    renderPublicAttendeeRoute,
    whoami.authenticated,
    whoami.loading,
    demo.loading,
    demoSignInAvailable,
    demo.unreachable,
  ]);

  // CD's signOut awaits logout before broadcasting signedIn=false. The host
  // only re-resolves whoami to re-arm the demo gate or hosted-login redirect.
  const hadSession = useRef(false);
  // The person the post-sign-in whoami refresh below last ran for (cleared on sign-out).
  const refreshedForSignIn = useRef<string | null>(null);
  // Cover the transition render itself, before the passive refresh effect:
  // otherwise CD's Signin mounts, is removed by loading, then mounts again.
  const signingOut = hadSession.current && !cdSignedIn;
  const refreshWhoami = whoami.refresh;

  useEffect(() => {
    if (cdSignedIn) {
      hadSession.current = true;
      return;
    }
    if (!hadSession.current) return;
    hadSession.current = false;
    refreshedForSignIn.current = null;
    refreshWhoami();
  }, [cdSignedIn, refreshWhoami]);

  // The sign-IN counterpart. CD's in-page persona sign-in mints the session and
  // flips `useAuth().signedIn` without reloading the page, so the host's whoami
  // still holds the signed-out answer it resolved at boot. Until it re-resolves,
  // `setAuthenticated` (below) never runs and cd-app's `can()` answers from its
  // client role catalogue instead of the capability list the server enforces: a
  // System Administrator was offered a Settings tab the server then refused. So
  // re-resolve once per signed-in person; a ref (cleared on sign-out) stops a
  // second refresh for the same person, which also rules out a loop if the two
  // ids never agree.
  const hostLacksSignedInPerson =
    cdSignedIn && cdUserId !== "" && (!whoami.authenticated || session.user.id !== cdUserId);
  useEffect(() => {
    if (!hostLacksSignedInPerson || whoami.loading) return;
    if (refreshedForSignIn.current === cdUserId) return;
    refreshedForSignIn.current = cdUserId;
    refreshWhoami();
  }, [hostLacksSignedInPerson, whoami.loading, cdUserId, refreshWhoami]);
  // While that refresh is in flight cd-app is already signed in and mounted. The
  // loading branch below must not swap it for the placeholder: an unmount resets
  // CD's screen state between sign-in and its first screen.
  const refreshingAfterSignIn =
    whoami.loading && cdSignedIn && cdUserId !== "" && refreshedForSignIn.current === cdUserId;

  // Packet-3 sign-in composition (v0y4ge/voyage-fleet-management, cd-drop3-ingest):
  // CD's own App.tsx now wraps AppRouter in an AuthGate (`useAuth().signedIn`,
  // shell/access.ts) that shows CD's OWN <Signin/> persona chooser whenever
  // access.ts's internal SIGNED_IN flag is false. Previously this effect called
  // the plain `setUser()` (hydrate CURRENT_USER only); left unchanged, a
  // real-SSO-authenticated tenant user would reach this branch, CDApp would
  // mount, and CD's AuthGate would immediately show ITS OWN Signin again
  // (SIGNED_IN defaults false) — a redundant second sign-in prompt on top of
  // the tenant-native login this file already resolved above. `setAuthenticated`
  // is the new exported setter CD added for exactly this host-driven bypass
  // (see access.ts's own doc comment on the function): it hydrates CURRENT_USER
  // AND flips SIGNED_IN, so CD's AuthGate renders AppRouter directly and its
  // own Signin never appears for a session this wrapper already resolved.
  // This is the ONLY change to this effect. `renderDemoSignIn` (below) is
  // unaffected: it keeps deciding whether the FIRST unauthenticated hit for
  // a demo_mode tenant is allowed to reach CDApp at all (vs. redirected to
  // the tenant's real SSO login); once there, CD's own Signin is what
  // renders for that first hit, and if that session round-trips through a
  // real reload/whoami resolution afterward, THIS effect is what reconciles
  // it into the host's role-mapped CurrentUser via `setAuthenticated`.
  useEffect(() => {
    if (whoami.loading || !whoami.authenticated) return;
    const email = session.user.email.trim();
    if (!email) return;
    const mappedRole = mapMembershipRoleToStudioRole(session.role);
    // Session-refresh role-collapse fix (2026-07-28): assignments carry
    // through UNCHANGED from useWhoami (already normalized onto studio-role
    // labels there) -- this is the per-process bundle
    // `setAuthenticated`/`effectivePerms` (cd-app/shell/access.ts) actually
    // resolve capabilities from; dropping it (the pre-fix behavior) is what
    // collapsed every non-Events persona to zero capabilities on reload.
    const assignments = session.assignments;
    // Second defect (the fallback): if reload resolves NEITHER a catalog
    // role label NOR any surviving assignment, there is nothing to derive a
    // capability from. Skip `setAuthenticated` entirely rather than hydrate
    // a CurrentUser that can only ever hit NoAccess -- SIGNED_IN stays
    // false, so CD's own AuthGate falls back to its sign-in / persona
    // chooser instead of painting the role-denial screen for a session that
    // simply failed to resolve.
    // Residual split-brain fix (2026-07-28): a value is "resolvable" when it
    // is either a real ROLE_CATALOG label OR a tenant-defined CUSTOM role
    // (access.ts's CUSTOM_ROLES, surfaced via getCustomRoles() -- the SAME
    // acceptance getRolePerms()/effectivePerms() already apply). Gating this
    // check on STUDIO_ROLE_LABELS alone (catalog-only) meant a custom-role
    // assignment that survived BOTH whoami parsers unchanged (mapAssignments
    // passes any dot-free role id through verbatim -- see useWhoami.ts) still
    // got rejected here on reload, even though the fresh in-app sign-in path
    // (access.ts's signIn(), which hydrates CURRENT_USER from whoami with no
    // catalog-only filter at all) accepts that exact same value outright --
    // so a custom-role session derived DIFFERENT authenticated state on
    // reload vs. fresh sign-in. A value unknown to BOTH the catalog AND the
    // tenant's custom-role set is still rejected -- this widens acceptance,
    // it does not remove it.
    const isKnownRoleLabel = (value: string): boolean =>
      STUDIO_ROLE_LABELS.has(value) || Object.prototype.hasOwnProperty.call(getCustomRoles(), value);
    const hasResolvableRole = Boolean(mappedRole) && isKnownRoleLabel(mappedRole as string);
    const hasResolvableAssignment = assignments
      ? Object.values(assignments).some((value) => isKnownRoleLabel(value))
      : false;
    // That fallback is for a demo-persona session only (whoami.demoPersona). A real
    // sign-in (password, Microsoft or SAML) carries no role label or assignments: its
    // membership slug always maps to some label (a viewer maps to "Viewer", which the
    // catalogue does not list) and whoami's capability list decides what it may open.
    // Skipping it left a real person on the demo persona chooser after a successful
    // single sign-on, so a real session is always signed in here.
    // A demo session whose whoami carries capabilities is still a resolved
    // session: the served list below is what `can()` decides on, so the
    // unknown label only costs the role-derived fallback. Skipping it here left
    // a reload on the persona chooser while whoami still answered signed in.
    const hasServedCapabilities = (session.capabilities?.length ?? 0) > 0;
    if (whoami.demoPersona === true && !hasResolvableRole && !hasResolvableAssignment && !hasServedCapabilities) return;
    // The identity worker's `user.role` is a narrative demo-persona title when
    // the persona has process-scoped assignments (for example, "Managing
    // Director" alongside an Events System Administrator assignment). It is
    // not a membership-role slug. Keep that title for display once the session
    // has an independently resolvable assignment or server-authoritative
    // capabilities; permissions still come only from those assignments and/or
    // the capability snapshot below. Membership slugs continue through the
    // mapper, and unresolved demo sessions still fail closed above.
    const narrativeRole = session.role?.trim() ?? "";
    const preserveNarrativeRole =
      whoami.demoPersona === true &&
      narrativeRole.length > 0 &&
      mappedRole === "Viewer" &&
      narrativeRole.toLowerCase() !== "viewer" &&
      (hasResolvableAssignment || hasServedCapabilities);
    const role = preserveNarrativeRole ? narrativeRole : mappedRole;
    const name = displayNameFromSession(session.user.displayName, email);
    const init = session.user.initials.trim() || initialsFromName(name);
    setAuthenticated({
      id: session.user.id || undefined,
      name,
      email,
      init,
      color: "#5142C7",
      role,
      workspaceRole: session.workspaceRole ?? null,
      assignments,
      // whoami's `capabilities` is the SAME list the edge gateway enforces on,
      // so it is the authoritative answer to "may this user open this surface".
      // Passing null here discarded it and left `can()` to fall back to
      // `effectivePerms()`, which derives capabilities from the role label
      // alone -- and a non-demo session carries no per-process assignments, so
      // `mapMembershipRoleToStudioRole` collapses every ordinary member to
      // "Event Creator". A user the server had granted schedules.read was
      // therefore shown "Your role can't open this surface" on Workforce while
      // the API would have served them. Preferring the served list makes the UI
      // agree with the enforcement point; the role-derived fallback still
      // applies whenever whoami carries no capabilities.
      //
      // The served list is CANONICAL vocabulary (identity.team.read,
      // appointments.read_any, ...). cd-app's route gates (shell/routeTable.ts
      // ROUTE_READ_PERM) still name the pre-canonical UI vocabulary for the
      // People/Staff surfaces (`team.read`) and for the Appointments surface
      // (`appointments.read`, from APPT_BRIDGE_CAPS), and `can()` checks the
      // snapshot by exact string -- so a System Administrator whose session
      // carried every canonical id was still refused both surfaces. The
      // projection widens the served list with the UI names each canonical id
      // stands for (STUDIO_UI_CAPABILITY_ALIASES, beside
      // CANONICAL_ROLE_ID_TO_STUDIO_ROLE in @v0y4ge/api-contracts settings.ts);
      // the canonical ids stay in place, an empty list stays empty (so the
      // role-derived fallback above is untouched), and nothing outside cd-app's
      // own snapshot ever sees the widened list. It is a shim: it retires when
      // CD re-points the route gates onto canonical ids.
      permissions: session.capabilities ? projectStudioUiCapabilities(session.capabilities) : null,
      locations: null,
    });
  }, [
    whoami.authenticated,
    whoami.loading,
    whoami.demoPersona,
    session.role,
    session.workspaceRole,
    session.assignments,
    session.capabilities,
    session.user.displayName,
    session.user.email,
    session.user.id,
    session.user.initials,
  ]);

  if (renderLocalSignIn) {
    return (
      <SignInScreen
        tenant={buildLocalSignInBranding()}
        onSubmit={async () => {
          window.location.href = "/dashboard";
        }}
      />
    );
  }

  // Also gate on `demo.loading` (#686): a demo_mode=on tenant must never
  // flash-redirect to SSO (or flash-render nothing) before the runtime probe
  // resolves — one extra paint of loading chrome beats a stale-flash race.
  if ((whoami.loading && !refreshingAfterSignIn) || (demo.loading && !whoami.authenticated && !renderPublicAttendeeRoute)) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  // Keep CD's signed-out screen unmounted until the host starts re-resolution.
  if (signingOut) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  // A demo_mode tenant's first unauthenticated hit mounts CDApp here too:
  // its AuthGate renders CD's OWN Signin (persona chooser) rather than a
  // studio-shell stand-in — see the `renderDemoSignIn` gate comment above.
  // This branch is reached AFTER whoami/demo have resolved but BEFORE the
  // redirect effect above has actually navigated the browser away (the
  // `window.location.assign(loginUrl)` call is async relative to this
  // render) — an unauthenticated, non-public, non-demo hit sits here for at
  // least one paint while the redirect is in flight. Returning `null` painted
  // a genuinely blank root for that window, which is what tenant#811
  // observed intermittently (~2/10 loads catch this render before the
  // navigation takes effect). Render the same loading placeholder used above
  // instead of `null` so the root never paints blank during that handoff.
  // `refreshingAfterSignIn`: cd-app is signed in and the host is re-resolving
  // whoami, which still holds the signed-out answer until it returns.
  // `renderDemoUnreachable`: CDApp shows CD's sign-in screen in its unreachable
  // state (see the gate above).
  if (
    !whoami.authenticated &&
    !renderPublicAttendeeRoute &&
    !renderDemoSignIn &&
    !renderDemoUnreachable &&
    !refreshingAfterSignIn
  ) {
    return <div aria-busy="true" style={{ minHeight: "100vh", background: "var(--color-bg, #f7f7fb)" }} />;
  }

  return <CDApp demoUnreachable={renderDemoUnreachable} onRetry={demo.retry} />;
}
